/**
 * Event Designer console — canonical creation and management UI for Events.
 *
 * Route: /prioritization/sessions/new (and /explore for legacy redirect).
 * Creates a real prioritization_sessions row on setup; navigates to the
 * existing SessionDetail for live/open collaborative steps.
 *
 * Permissions: EVENT_DESIGN capability (falls back to SESSION_RUN / admin).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ExternalLink, Layers, Loader2, MapPin,
  RotateCcw, Square, Radio, Clock, Circle, CheckCircle2, AlertCircle,
  ArrowRight, Sparkles, Info,
} from 'lucide-react';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { EngagementSetup } from './components/EngagementSetup';
import { RequestContextPanel } from './components/RequestContextPanel';
import { StepRail } from './components/StepRail';
import { StepPanel } from './components/StepPanels';
import type { PlatformItem } from './components/ItemInputModal';
import { getJob } from './sessionJobs';
import {
  unmetRequirements,
  resolveRequiredInformation,
  getStepStatus,
  isStepMandatory,
  getNextRightThing,
  getStepSummary,
  getReconfirmPrompt,
  isStepContentSatisfied,
  type StepStatus,
} from './engineLogic';
import { useSessionEngineStore } from './store';
import { STEP_META, STEP_ORDER, type ContainerMode, type StepId } from './types';
import {
  buildStepRailEntries,
  goalNeedsFrameContentFacet,
  type StepNavTarget,
} from './stepFacets';
import { useExchangeContext } from '../../hooks/prioritization/useExchangeContext';
import { offeredPlatformItems } from './offeredPlatformItems';
import {
  catchballQueryFromSearch,
  withCatchballQuery,
} from '../../lib/catchballSessionLink';

const SEED_PLATFORM_ITEMS: PlatformItem[] = [
  { id: 'item_seed_1', title: 'Launch mobile app' },
  { id: 'item_seed_2', title: 'Migrate billing system' },
  { id: 'item_seed_3', title: 'Redesign onboarding' },
  { id: 'item_seed_4', title: 'SOC 2 compliance' },
  { id: 'item_seed_5', title: 'Self-serve analytics' },
  { id: 'item_seed_6', title: 'Partner API' },
  { id: 'item_seed_7', title: 'Pricing experiment' },
  { id: 'item_seed_8', title: 'Internal admin tools' },
];

const STEP_STATUS_STYLE: Record<StepStatus, { dot: string; label: string; icon: typeof Circle }> = {
  not_started:     { dot: 'bg-[var(--surface)] dark:bg-granite-gray-400', label: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]', icon: Circle },
  available:       { dot: 'bg-slate-blue-500 dark:bg-slate-blue-300', label: 'text-slate-blue-700 dark:text-slate-blue-400', icon: Circle },
  in_progress:     { dot: 'bg-electric-mustard-500 dark:bg-electric-mustard-400 animate-pulse', label: 'text-electric-mustard-700 dark:text-electric-mustard-400', icon: Circle },
  needs_reconfirm: { dot: 'bg-luminous-coral-500 dark:bg-luminous-coral-400', label: 'text-luminous-coral-700 dark:text-luminous-coral-400', icon: AlertCircle },
  complete:        { dot: 'bg-alpine-green-600 dark:bg-alpine-green-500', label: 'text-alpine-green-700 dark:text-alpine-green-400', icon: CheckCircle2 },
};

export default function SessionEngineV3App() {
  const event = useSessionEngineStore((s) => s.event);
  const resetEvent = useSessionEngineStore((s) => s.resetEvent);
  const loadEvent = useSessionEngineStore((s) => s.loadEvent);
  const setContainerMode = useSessionEngineStore((s) => s.setContainerMode);

  const storeItems = useItemsStore((s) => s.items);
  const fetchFromSupabase = useItemsStore((s) => s.fetchFromSupabase);
  const [activeNav, setActiveNav] = useState<StepNavTarget>({ step: 'S1', facet: 'frame' });
  const [loadingExisting, setLoadingExisting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // /new route always starts fresh — clear any saved active event
  const isNewRoute = location.pathname.endsWith('/new');
  const isExploreRoute = location.pathname.endsWith('/explore');
  // ?resume=:id → load a specific existing event (e.g. from sessions list click)
  const resumeId = searchParams.get('resume');
  const exchangeContext = useExchangeContext(event?.contextMode === 'cycle_linked' ? event.cycleId : undefined);

  // Reload active event from localStorage if no in-memory event on mount.
  // /new without ?resume= is a fresh start. /new?resume=:id (Catchball
  // "Configure session") must load that Event Designer session — not Session Detail.
  useEffect(() => {
    if (resumeId) {
      setLoadingExisting(true);
      void loadEvent(resumeId).finally(() => setLoadingExisting(false));
      return;
    }
    if (isNewRoute) {
      // Explicit new-session intent — always clear saved state, even if one is in memory
      try { localStorage.removeItem('v3ActiveEventId'); } catch { /* noop */ }
      resetEvent();
      return;
    }
    if (isExploreRoute) {
      // Bare /explore is the dashboard entry. Resume requires an explicit query param.
      try { localStorage.removeItem('v3ActiveEventId'); } catch { /* noop */ }
      resetEvent();
      return;
    }
    if (event) return;
    const idToLoad = localStorage.getItem('v3ActiveEventId');
    if (idToLoad) {
      setLoadingExisting(true);
      void loadEvent(idToLoad).finally(() => setLoadingExisting(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Once an event is active, keep the URL in sync with the event ID so that
  // browser back/forward and sharing work correctly. Without this, the URL
  // stays at bare /explore and pressing back loses all context.
  // Preserve Catchball ?loopId=/ballId=/trackId=/cycleId= across resume sync.
  useEffect(() => {
    if (!event || event.persisting || event.id.startsWith('evt_')) return;
    const currentResume = searchParams.get('resume');
    if (currentResume !== event.id) {
      const next = new URLSearchParams(searchParams);
      next.set('resume', event.id);
      navigate(`?${next.toString()}`, { replace: true });
    }
  }, [event?.id, event?.persisting]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (storeItems.length === 0) {
      void fetchFromSupabase().catch(() => {});
    }
  }, [storeItems.length, fetchFromSupabase]);

  // When the event first loads with every Frame step the goal actually NEEDS
  // satisfied, jump to the Main Event — don't make the user wade through steps
  // they didn't need to fill. Optional Frame steps (2026-09-22 ruling: only the
  // goal's hard prerequisites are mandatory) stay in the rail for whoever wants
  // them, but they no longer stand between the creator and their goal.
  // Runs once per event id.
  const eventId = event?.id;
  useEffect(() => {
    if (!event) return;

    const goal = event.primaryGoalStep;
    const inScopeSteps = STEP_ORDER.filter((s) => event.steps[s].required);
    const { frame, mainEvent } = buildStepRailEntries(inScopeSteps, goal);
    const frameOutstanding = frame.filter(
      (t) => isStepMandatory(event, t.step) && !isStepContentSatisfied(event, t.step),
    );
    if (frameOutstanding.length === 0 && mainEvent) {
      setActiveNav(mainEvent);
      return;
    }
    const firstOpen =
      frameOutstanding[0] ?? frame.find((t) => !isStepContentSatisfied(event, t.step));
    if (firstOpen) setActiveNav(firstOpen);
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectStepId = (step: StepId) => {
    const goal = event?.primaryGoalStep;
    if (goal && step === goal) {
      // Prefer Frame item pool when goal is S2 and pool still empty.
      if (
        goalNeedsFrameContentFacet(goal) &&
        step === 'S2' &&
        event &&
        !isStepContentSatisfied(event, 'S2')
      ) {
        setActiveNav({ step: 'S2', facet: 'frame' });
        return;
      }
      setActiveNav({ step, facet: 'goal' });
      return;
    }
    setActiveNav({ step, facet: 'frame' });
  };

  const platformItems: PlatformItem[] = useMemo(() => {
    if (storeItems.length > 0) return offeredPlatformItems(storeItems);
    return SEED_PLATFORM_ITEMS;
  }, [storeItems]);

  if (loadingExisting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--surface)] dark:bg-[var(--bg)]">
        <div className="flex flex-col items-center gap-3 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Loader2 size={28} className="animate-spin" />
          <p className="text-sm">Loading your event…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface)] dark:bg-[var(--bg)]">
      {/* Top breadcrumb bar */}
      <div className="flex items-center gap-2 border-b border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-2 text-sm">
        <Link to="/prioritization/sessions" className="flex items-center gap-1 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]" title="Go to Sessions — use browser Back to return here">
          <ChevronLeft size={15} /> Sessions
        </Link>
        {event && (
          <>
            <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">/</span>
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-blue-700 dark:text-slate-blue-400">
              Event Designer
            </span>
          </>
        )}
        {event && (
          <>
            <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">/</span>
            <span className="truncate font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{event.title}</span>
          </>
        )}
        {event && !event.persisting && !event.id.startsWith('evt_') && (
          <div className="ml-auto flex items-center gap-2">
            <a
              href={withCatchballQuery(
                `/prioritization/sessions/${event.id}/live?startRanking=1&step=${activeNav.step}`,
                catchballQueryFromSearch(searchParams),
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
            >
              <Radio size={12} /> Open room
            </a>
            <Link
              to={withCatchballQuery(
                `/prioritization/sessions/${event.id}`,
                catchballQueryFromSearch(searchParams),
              )}
              className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
            >
              Admin <ExternalLink size={11} />
            </Link>
          </div>
        )}
      </div>

      {!event ? (
        <div className="px-4 py-6">
          <EngagementSetup />
        </div>
      ) : event.persisting ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            <Loader2 size={28} className="animate-spin" />
            <p className="text-sm">Creating event…</p>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-6xl px-4 py-5">
          {/* Event header */}
          {/* Stacks on phones. This was `flex flex-wrap items-center` with a
              `flex-1 min-w-0` title next to a two-button container toggle whose
              labels ("Single session" / "Multi-session event") do not shrink —
              so at 375px the title column collapsed to a few characters wide,
              the heading wrapped one word per line, and the cycle/track pill
              rendered on top of the toggle. */}
          <header className="mb-5 flex flex-col gap-3 border-b border-[var(--rule)] dark:border-[var(--rule-bright)] pb-4 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="w-full min-w-0 sm:flex-1">
              <h1 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{event.title}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-0.5">
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  {getJob(event.jobId)?.title ?? 'Custom event'} · {event.containerMode === 'single' ? 'single session' : 'multi-session event'}
                </p>
                {event.contextMode === 'cycle_linked' ? (
                  <>
                    <span className="inline-flex max-w-full items-start gap-1 rounded-full bg-slate-blue-100 dark:bg-slate-blue-900/40 px-2 py-0.5 text-[11px] font-medium text-slate-blue-800 dark:text-slate-blue-200">
                      <MapPin size={10} className="mt-[3px] flex-shrink-0" />
                      <span className="min-w-0 break-words">
                        Cycle: {event.cycleName ?? event.cycleId} · Track: {event.trackName ?? event.trackId}
                      </span>
                    </span>
                    {exchangeContext.exchangeState !== 'not_applicable' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-electric-mustard-100 dark:bg-electric-mustard-900/30 px-2 py-0.5 text-[11px] font-medium text-electric-mustard-800 dark:text-electric-mustard-300">
                        Exchange: {exchangeContext.exchangeState === 'revision_received' ? 'Revision received' : 'Request received'}
                      </span>
                    )}
                  </>
                ) : event.contextMode === 'app_linked' ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    App-linked
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                    Isolated
                  </span>
                )}
              </div>
            </div>

            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="inline-flex border border-[var(--rule)] dark:border-[var(--rule-bright)] p-0.5 text-xs">
              {(['single', 'multi'] as ContainerMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setContainerMode(m)}
                  className={[
                    'flex items-center gap-1  px-2.5 py-1.5 font-medium capitalize transition',
                    event.containerMode === m ? 'bg-slate-blue-600 text-white' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]',
                  ].join(' ')}
                >
                  {m === 'single' ? <Square size={12} /> : <Layers size={12} />}
                  {m === 'single' ? 'Single session' : 'Multi-session event'}
                </button>
              ))}
            </div>

            <button
              onClick={resetEvent}
              className="flex items-center gap-1.5 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-1.5 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
            >
              <RotateCcw size={13} /> Start over
            </button>
            </div>
          </header>

          {/* F6.4: the request the ball carried, readable while designing the
              response to it — not just a badge saying one exists. */}
          <RequestContextPanel
            intent={exchangeContext.intent}
            isRevision={exchangeContext.exchangeState === 'revision_received'}
            className="mb-4"
          />

          {event.containerMode === 'multi' && (
            <p className="mb-4 bg-slate-blue-50 dark:bg-slate-blue-900/30 px-3 py-2 text-xs text-slate-blue-800 dark:text-slate-blue-200">
              <strong>Multi-session event:</strong> Each collaborative step becomes a child session referencing this parent.{' '}
              {STEP_ORDER.filter((s) => event.steps[s].required && event.steps[s].mode === 'collaborative').length} of {STEP_ORDER.filter((s) => event.steps[s].required).length} steps are collaborative.
            </p>
          )}

          <div className="grid gap-6 md:grid-cols-[16rem_1fr]">
            <aside>
              <StepRail active={activeNav} onSelect={setActiveNav} />
              <ConditionsMetDashboard activeStep={activeNav.step} onSelect={selectStepId} />
              <SessionProgression onSelect={selectStepId} />
            </aside>
            <main className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-sm dark:shadow-none ring-1 ring-gray-200 dark:ring-[var(--rule-bright)]">
              {/* Active step header */}
              <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] px-5 pt-5 pb-3">
                <StepHeader step={activeNav.step} facet={activeNav.facet} />
              </div>
              <div className="p-5">
                <StepPanel
                  step={activeNav.step}
                  facet={activeNav.facet}
                  platformItems={platformItems}
                  onSelectStep={selectStepId}
                />
              </div>
            </main>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Step header: shows step name, its status, a reconfirm warning if needed,
 * and a summary of what's currently set so the designer can reconfirm.
 */
function StepHeader({ step, facet }: { step: StepId; facet: 'frame' | 'goal' }) {
  const event = useSessionEngineStore((s) => s.event);
  if (!event) return null;

  const status = getStepStatus(event, step);
  const summary = getStepSummary(event, step);
  const reconfirmMsg = getReconfirmPrompt(event, step);
  const meta = STEP_META[step];
  const style = STEP_STATUS_STYLE[status];

  const isFramePool =
    facet === 'frame' && step === 'S2' && event.primaryGoalStep === 'S2';
  const isGoalStep = facet === 'goal';

  return (
    <div>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {!isGoalStep && (
              <span className="rounded bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                The Frame
              </span>
            )}
            {isGoalStep && (
              <span className="rounded bg-electric-mustard-100 dark:bg-electric-mustard-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-electric-mustard-800 dark:text-electric-mustard-300">
                Main Event
              </span>
            )}
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {isFramePool ? 'S2 — Item pool' : `${step} — ${meta.label}`}
            </h2>
          </div>
          <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {isFramePool
              ? 'Declare the manageable set for criteria and values. Collaborative S2 runs from Main Event.'
              : meta.blurb}
          </p>
        </div>
        <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${style.label} bg-opacity-10`}>
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${style.dot}`} />
          {statusLabel(status)}
        </span>
      </div>

      {/* Previously set snapshot — lets designer reconfirm */}
      {summary && (
        <div className="mt-2 flex items-center gap-1.5 bg-[var(--surface)] dark:bg-[var(--surface)] px-2.5 py-1.5 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Info size={11} className="shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          Currently: <span className="font-medium">{summary}</span>
        </div>
      )}

      {/* Reconfirm warning */}
      {reconfirmMsg && (
        <div className="mt-2 flex items-start gap-1.5 bg-luminous-coral-100 dark:bg-luminous-coral-900/30 px-2.5 py-1.5 text-[11px] text-luminous-coral-800 dark:text-luminous-coral-300">
          <AlertCircle size={11} className="mt-0.5 shrink-0" />
          {reconfirmMsg}
        </div>
      )}
    </div>
  );
}

function statusLabel(status: StepStatus): string {
  switch (status) {
    case 'not_started': return 'Not started';
    case 'available': return 'Ready to set';
    case 'in_progress': return 'In progress';
    case 'needs_reconfirm': return 'Needs review';
    case 'complete': return 'Complete';
  }
}

/**
 * Conditions-Met Dashboard — the map between where you are and what's needed.
 * Only shows required steps for this event's goal.
 */
function ConditionsMetDashboard({ activeStep, onSelect }: { activeStep: StepId; onSelect: (step: StepId) => void }) {
  const event = useSessionEngineStore((s) => s.event);
  if (!event) return null;

  const goalStep = event.primaryGoalStep;
  const unmet = unmetRequirements(event);
  // Anything still unmet is listed even when the frame did not mark it
  // required — otherwise the one step blocking the run is the one step the
  // user cannot click through to.
  const unmetSteps = new Set(unmet.map((u) => u.step));
  const requiredSteps = STEP_ORDER.filter((s) => event.steps[s].required || unmetSteps.has(s));
  const requiredInformation = event.jobId ? resolveRequiredInformation(event) : [];
  const nextStep = getNextRightThing(event);
  const nextMeta = STEP_META[nextStep];
  const nextStatus = getStepStatus(event, nextStep);
  const allComplete = requiredSteps.every((s) => getStepStatus(event, s) === 'complete');
  const goalLabel = goalStep ? `${goalStep} — ${STEP_META[goalStep].label}` : 'the main event';

  return (
    <div className="mt-4 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-3 text-xs">
      <p className="mb-2 font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Conditions met</p>
      {requiredInformation.length > 0 && (
        <div className="mb-3 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] p-2">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Pre-flight checklist
          </p>
          <ul className="space-y-1">
            {requiredInformation.map((item) => {
              const StepIcon = item.step ? STEP_STATUS_STYLE[getStepStatus(event, item.step)].icon : Circle;
              const itemClass = item.step
                ? STEP_STATUS_STYLE[getStepStatus(event, item.step)].label
                : 'text-electric-mustard-700 dark:text-electric-mustard-400';
              return (
                <li key={item.id} className="flex items-start gap-1.5 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  <StepIcon size={11} className={`mt-0.5 shrink-0 ${itemClass}`} />
                  <span className="flex-1 leading-snug">
                    {item.label}
                    {item.source === 'job' && <span className="ml-1 text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">job input</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <ul className="space-y-1">
        {requiredSteps.map((step) => {
          const status = getStepStatus(event, step);
          const style = STEP_STATUS_STYLE[status];
          const Icon = style.icon;
          const summary = getStepSummary(event, step);
          const isGoal = step === goalStep;
          return (
            <li key={step}>
              <button
                onClick={() => onSelect(step)}
                className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              >
                <Icon size={12} className={style.label} />
                <span className={`flex-1 font-medium ${status === 'complete' ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'}`}>
                  {step} {STEP_META[step].label}
                  {isGoal && <span className="ml-1 text-[10px] text-electric-mustard-700 dark:text-electric-mustard-400 font-normal">goal</span>}
                </span>
                {summary && (
                  <span className="truncate text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] max-w-[80px]" title={summary}>
                    {summary}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-2">
        {allComplete ? (
          <p className="text-alpine-green-700 dark:text-alpine-green-400 font-medium">
            ✓ All conditions met — {goalLabel} is ready to run.
          </p>
        ) : (() => {
          // Filter out the goal step itself from the "needs" list — saying "S4 needs S4" is nonsensical.
          const blockers = unmet.filter((u) => u.step !== goalStep);
          if (blockers.length > 0) {
            return (
              <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                Still needed: {blockers.map((u) => `${u.step} ${STEP_META[u.step].label}`).join(', ')}
              </p>
            );
          }
          return (
            <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Prerequisites met — work on {goalLabel} to complete it.
            </p>
          );
        })()}
      </div>

      {/* Only show the NRT button when the user is NOT already on that step —
          if they're already there, they're doing it. One primary action at a time. */}
      {!allComplete && nextStep !== activeStep && (
        <button
          onClick={() => onSelect(nextStep)}
          className="mt-2 flex w-full items-center gap-1.5 bg-slate-blue-600 px-3 py-1.5 font-medium text-white hover:bg-slate-blue-700"
        >
          <Sparkles size={11} />
          <span className="flex-1 text-left">
            {nextStatus === 'needs_reconfirm' ? 'Review' : 'Work on'}{' '}
            {nextStep} — {nextMeta.label}
          </span>
          <ArrowRight size={11} />
        </button>
      )}
    </div>
  );
}

/**
 * Shows each collaborative step as a launchable session.
 */
function SessionProgression({ onSelect }: { onSelect: (step: StepId) => void }) {
  const event = useSessionEngineStore((s) => s.event);
  const startStepRun = useSessionEngineStore((s) => s.startStepRun);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inboxCtx = catchballQueryFromSearch(searchParams);
  const [launchingStep, setLaunchingStep] = useState<StepId | null>(null);
  if (!event) return null;

  const collaborative = STEP_ORDER.filter((s) => event.steps[s].mode === 'collaborative');
  const isMultiSession = event.containerMode === 'multi';
  const sessionId = event.id && !event.id.startsWith('evt_') ? event.id : null;

  async function launch(step: StepId) {
    if (!sessionId || launchingStep) return;
    setLaunchingStep(step);
    try {
      const targetSessionId = await startStepRun(step);
      if (targetSessionId) {
        window.open(
          withCatchballQuery(
            `/prioritization/sessions/${targetSessionId}/live?startRanking=1&step=${step}&invited=1`,
            inboxCtx,
          ),
          '_blank',
          'noopener,noreferrer',
        );
      }
    } finally {
      setLaunchingStep(null);
    }
  }

  return (
    <div className="mt-4 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-3 text-xs">
      <p className="mb-1 font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Sessions</p>
      <p className="mb-2 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        {isMultiSession
          ? 'Each collaborative step runs as its own child session.'
          : 'One working session — all collaborative steps run inline.'}
      </p>
      {collaborative.length === 0 ? (
        <p className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">All steps are declared — nothing to run.</p>
      ) : (
        <ul className="space-y-1.5">
          {collaborative.map((step) => {
            const cfg = event.steps[step];
            const StatusIcon =
              cfg.runStatus === 'complete' ? CheckCircle2
              : cfg.runStatus === 'running' ? (cfg.runMode === 'live' ? Radio : Clock)
              : Circle;
            const statusCls =
              cfg.runStatus === 'complete' ? 'text-alpine-green-700 dark:text-alpine-green-400'
              : cfg.runStatus === 'running' ? 'text-electric-mustard-700 dark:text-electric-mustard-400'
              : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]';
            return (
              <li key={step} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                <button
                  onClick={() =>
                    cfg.runStatus === 'running' && sessionId
                      ? navigate(
                          withCatchballQuery(
                            `/prioritization/sessions/${event.childSessions[step] ?? sessionId}/live?step=${step}`,
                            inboxCtx,
                          ),
                        )
                      : onSelect(step)
                  }
                  className="flex flex-1 items-center gap-2 text-left"
                >
                  <StatusIcon size={13} className={statusCls} />
                  <span className="flex-1 text-[var(--text-pri)] dark:text-[var(--text-pri)]">{step} {STEP_META[step].label}</span>
                  <span className="text-[10px] uppercase text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    {cfg.runStatus === 'not_started'
                      ? isMultiSession ? 'child' : cfg.runMode
                      : cfg.runStatus === 'running' ? `${cfg.runMode}…`
                      : 'done'}
                  </span>
                </button>
                {cfg.runStatus === 'not_started' && sessionId && (
                  <button
                    onClick={() => void launch(step)}
                    disabled={launchingStep === step}
                    className="flex items-center gap-1 rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:border-alpine-green-600 hover:bg-alpine-green-50 dark:hover:bg-alpine-green-900/30 hover:text-alpine-green-700 dark:hover:text-alpine-green-300"
                  >
                    {launchingStep === step ? <Loader2 size={10} className="animate-spin" /> : cfg.runMode === 'live' ? <Radio size={10} /> : <Clock size={10} />}
                    {launchingStep === step ? 'Starting' : cfg.runMode === 'live' ? 'Go live' : 'Open'}
                  </button>
                )}
                {cfg.runStatus === 'running' && sessionId && (
                  <a
                    href={withCatchballQuery(
                      `/prioritization/sessions/${event.childSessions[step] ?? sessionId}/live?startRanking=1&step=${step}&invited=1`,
                      inboxCtx,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 rounded bg-alpine-green-700 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-alpine-green-600"
                  >
                    <ExternalLink size={10} /> Open
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
